import { useState } from "react";
import styled from "@emotion/styled";
import { MdsIcon } from "@/design-system/components/icon/MdsIcon";
import { MdsIconKind } from "@/design-system/components/icon";
import { MDSText, MDSTextOverflow, MDSTextVariant } from "@/mds";
import type { FC, ReactNode } from "react";

export interface CollapsibleSectionHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title?: string | null;
  children: ReactNode;
  defaultCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
}

export const CollapsibleSectionHeader: FC<CollapsibleSectionHeaderProps> = ({
  title,
  children,
  defaultCollapsed = false,
  onCollapseChange,
  className,
  ...props
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isHovered, setIsHovered] = useState(false);

  const sectionTitle = title ?? "Section";

  const toggle = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    onCollapseChange?.(next);
  };

  return (
    <>
      <SectionHeaderContainer
        {...props}
        className={className}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={toggle}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={!isCollapsed}
      >
        <SectionHeaderButton $isHovered={isHovered}>
          <SectionHeaderIcon $isCollapsed={isCollapsed}>
            <MdsIcon kind={isCollapsed ? MdsIconKind.CaretRight : MdsIconKind.CaretDown} />
          </SectionHeaderIcon>
        </SectionHeaderButton>
        <SectionHeaderTitle variant={MDSTextVariant.TextXs} overflow={MDSTextOverflow.Regular}>
          {sectionTitle}
        </SectionHeaderTitle>
      </SectionHeaderContainer>
      {!isCollapsed && children}
    </>
  );
};

const SectionHeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding-top: ${({ theme }) => theme.spacing.sm};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  padding-left: ${({ theme }) => theme.spacing.sm};
  padding-right: ${({ theme }) => theme.spacing.sm};
  min-height: 32px;
  align-self: stretch;
  width: 100%;
  cursor: pointer;
`;

const SectionHeaderButton = styled.button<{ $isHovered: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: ${({ theme, $isHovered }) =>
    $isHovered
      ? theme.colors.background.button.transparent.hover
      : theme.colors.background.button.transparent.default};
  border: none;
  cursor: pointer;
  border-radius: 100px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.button.transparent.hover};
  }

  & svg {
    font-size: 11px;
    color: ${({ theme }) => theme.colors.icon.secondary};
    margin-top: -1px;
  }
`;

const SectionHeaderIcon = styled.div<{ $isCollapsed: boolean }>`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.grey.x500};
  transition: transform 150ms ease;
`;

const SectionHeaderTitle = styled(MDSText)`
  text-transform: uppercase;
  letter-spacing: 0.24px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  flex: 1;
  text-align: left;
  max-width: calc(100% - 48px);
`;
